import { createSlice, createAsyncThunk, createEntityAdapter } from "@reduxjs/toolkit";
import { request } from "@/utils/api";

// Create entity adapter
const employeesLoginAdapter = createEntityAdapter({
    selectId: (employee) => employee._id,
});

// Async Thunks
export const fetchEmployeesLogin = createAsyncThunk(
    "employeesLogin/fetch",
    async (_, { rejectWithValue }) => {
        try {
            const response = await request({
                endpoint: "/admin/getEmployees",
                method: "GET",
            });

            if (!response.data || !Array.isArray(response.data)) {
                return rejectWithValue(response.error.message || "Failed to fetch employees.");
            }

            return response.data;
        } catch ({ error }) {
            return rejectWithValue(error.message || "An unexpected error occurred while fetching employees.");
        }
    }
);

export const saveEmployeesLogin = createAsyncThunk(
    "employeesLogin/save",
    async ({ name, email, password, phone, permissions }, { rejectWithValue }) => {
        try {
            const response = await request({
                endpoint: "/admin/addEmployee",
                method: "POST",
                body: {
                    name,
                    email,
                    password,
                    phone,
                    permissions,
                },
            });

            if (!response.success || !response.data) {
                return rejectWithValue(response.error.message || "Failed to save employee.");
            }

            return response.data;
        } catch ({ error }) {
            return rejectWithValue(error.message || "An unexpected error occurred while saving the employee.");
        }
    }
);

export const removeEmployeesLogin = createAsyncThunk(
    "employeesLogin/remove",
    async (id, { rejectWithValue }) => {
        try {
            const response = await request({
                endpoint: "/admin/deleteEmployee",
                method: "DELETE",
                params: { id },
            });

            if (!response.success) {
                return rejectWithValue(response.error.message || "Failed to remove employee.");
            }

            return id;
        } catch ({ error }) {
            return rejectWithValue(error.message || "An unexpected error occurred while removing the employee.");
        }
    }
);

// Initial state
const initialState = employeesLoginAdapter.getInitialState({
    loading: false,
    error: null,
});

// Slice
const employeesLoginSlice = createSlice({
    name: "employeesLogin",
    initialState,
    reducers: {
        clearEmployeesLogin: (state) => {
            employeesLoginAdapter.removeAll(state);
            state.loading = false;
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchEmployeesLogin.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchEmployeesLogin.fulfilled, (state, action) => {
                employeesLoginAdapter.setAll(state, action.payload);
                state.loading = false;
            })
            .addCase(fetchEmployeesLogin.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            .addCase(saveEmployeesLogin.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(saveEmployeesLogin.fulfilled, (state, action) => {
                employeesLoginAdapter.upsertOne(state, action.payload);
                state.loading = false;
            })
            .addCase(saveEmployeesLogin.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            .addCase(removeEmployeesLogin.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(removeEmployeesLogin.fulfilled, (state, action) => {
                employeesLoginAdapter.removeOne(state, action.payload);
                state.loading = false;
            })
            .addCase(removeEmployeesLogin.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            });
    },
});

// Selectors

export const {
    selectAll: selectAllEmployeesLogin,
    selectById: selectEmployeesLoginById,
    selectIds: selectEmployeesLoginIds,
} = employeesLoginAdapter.getSelectors((state) => state.employeesLogin);

// Actions & Reducer Export
export const { clearEmployeesLogin } = employeesLoginSlice.actions;
export default employeesLoginSlice.reducer;